import { MANUALLY_SELECTED_ORGANIZATION } from '@backoffice-frontend/common'
import {
  ENV,
  Environment,
  isAdminPanel,
  isLocalhost,
  isPrDeployment,
  mockServerPort,
} from '@backoffice-frontend/environment'
import { useCallback, useEffect, useState } from 'react'
import store from 'store'
import { getSubdomainFromHostname } from './utils'

export const CognitoIdentityProviderStages: Record<Environment, string> = {
  [Environment.test]: `http://localhost:${mockServerPort}/api/cognito-identity-provider`,
  [Environment.local]: `http://localhost:${mockServerPort}/api/cognito-identity-provider`,
  [Environment.development]:
    'https://cognito-identity-provider.backoffice.dev.moia-group.io',
  [Environment.integration]:
    'https://cognito-identity-provider.backoffice.int.moia-group.io',
  [Environment.production]:
    'https://cognito-identity-provider.backoffice.prd.moia-group.io',
  [Environment['production.us-east-1']]:
    'https://cognito-identity-provider.backoffice.prd.us-east-1.moia-group.io',
}

function getOrganizationFromSubdomain(): string | null {
  if (isAdminPanel) {
    return 'moia'
  }

  if (isLocalhost || isPrDeployment) {
    const selectedOrg = store.get(MANUALLY_SELECTED_ORGANIZATION)
    return selectedOrg ?? 'moia'
  }

  return getSubdomainFromHostname(window.location.hostname)
}

function getCognitoProviderAPIUrl(organization: string | null): string {
  const baseUrl = CognitoIdentityProviderStages[ENV]
  return `${baseUrl}/cognito-identity-provider/${organization}`
}

export function useCognitoIdentityProvider() {
  const [cognitoIdentityProviderName, setCognitoIdentityProviderName] =
    useState<string | null>(null)
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [noOrganizationFound, setNoOrganizationFound] = useState(false)

  const fetchCognitoIdentityProvider = useCallback(async () => {
    setLoading(true)
    setCognitoIdentityProviderName(null)
    setOrganizationId(null)
    setNoOrganizationFound(false)
    try {
      const organization = getOrganizationFromSubdomain()
      const endpoint = getCognitoProviderAPIUrl(organization)

      const response = await fetch(endpoint)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error ?? 'Failed to fetch auth provider')
      }

      const data = await response.json()
      setCognitoIdentityProviderName(data.cognitoIdentityProviderName)
      setOrganizationId(data.organizationId)
    } catch (_err) {
      const err = _err as { message: string }
      // Only an unknown subdomain means there is no organization. A known
      // organization without an identity provider still has a working
      // email/password login, so it must not be sent to the domain redirect.
      if (err.message.includes('No organization found')) {
        setNoOrganizationFound(true)
      }
      console.error('Error fetching Cognito client:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCognitoIdentityProvider()
  }, [fetchCognitoIdentityProvider])

  return {
    cognitoIdentityProviderName,
    organizationId,
    noOrganizationFound,
    loading,
    refetch: fetchCognitoIdentityProvider,
  }
}
